import CryptoJS from 'crypto-js'
import { machineId } from 'node-machine-id'
import NodeRSA from 'node-rsa'
import { ok } from 'node:assert'
import { X509Certificate } from 'node:crypto'
import { postRequest } from './httpRequest'

export type TLicenseManagerConfig = {
  server?: string
  tenantId: number
  productIdentifier: string
  autoRenewEnabled?: boolean
  offlineMode?: boolean
  renewOnInit?: boolean
  autoRenewOffset?: number
  loadCertStr: () => Promise<TLicenseBlock>
  saveCertStr: (cert: TLicenseBlock) => Promise<void>
  collectUsageMetrics?: () => Promise<Array<TUsageMetric>>
  collectPassthroughData?: () => Promise<TPassthroughData>
  deviceFingerprint?: () => string | Promise<string>
  onFeatureChange?: (features: TFeatures) => any
  logger?: TLogger
}

export type TLicenseBlock = string

export type TLicenseContainer = {
  licenseKey: string
  x509: string
}

export type TFeatures = { [key: string]: boolean | number | string }

export type TUsageMetric = { name: string; value: number }

export type TPassthroughData = Record<string, unknown>

export type TMetadata = {
  [key: string]: boolean | number | string | Array<any> | {}
}

export type TEntitlement = {
  id: string
  productId: string
  productMetadata: TMetadata
  features: TFeatures
  featureOverrides: TFeatures
  validFrom: Date
  validTo: Date
  isFloatable: boolean
}

export type TLicenseCertObj = {
  consumerId: string
  consumerRef?: string
  version: number
  tenantId: number
  renewalToken: string
  deviceLock: boolean
  deviceFingerprint: string
  createdAt: Date
  issuedAt: Date
  expiresAt: Date
  terminatesAt: Date
  entitlements: TEntitlement[]
  /**
   * Indicates the number of floating entitlements that are not attached to the consumer
   * because they are in use elsewhere at the time of renewal.
   */
  detachedEntitlementsCount: number
  managementJwt: string
  /**
   * Specifies whether the license is ephemeral and therefore does not have corresponding entries in the license server DB.
   */
  isEphemeral: boolean
}

export type TLogger = {
  error: Function
  warn: Function
  info: Function
  debug: Function
}

type SingleTimer = NodeJS.Timeout
type RepeatingTimer = NodeJS.Timeout

const enum LogLevel {
  ERROR = 'error',
  WARN = 'warn',
  INFO = 'info',
  DEBUG = 'debug',
}

export class LicenseManager {
  public config: TLicenseManagerConfig

  private initializationPromise?: Promise<void>
  private key?: NodeRSA
  private logger: TLogger
  protected licenseCert?: TLicenseCertObj
  private x509Cert?: X509Certificate
  private x509IssuerCert: X509Certificate
  private deviceFingerprint?: string
  private renewalTimer?: RepeatingTimer
  private upcomingEntitlementChangesCheckTimer?: RepeatingTimer
  private entitlementChangeTimer?: SingleTimer
  private currentFeatures?: TFeatures
  private isShuttingDown = false

  get isInitialized() {
    return !!this.initializationPromise
  }

  constructor(config: TLicenseManagerConfig) {
    this.config = config

    if (this.config.logger) {
      this.logger = this.config.logger
    } else {
      this.logger = {
        error() {
          console.log('ERROR:', ...arguments)
        },
        warn() {
          console.log('WARN:', ...arguments)
        },
        info() {
          console.log('INFO:', ...arguments)
        },
        debug() {
          console.log('DEBUG:', ...arguments)
        },
      }
    }

    this.x509IssuerCert = new X509Certificate(
      '-----BEGIN CERTIFICATE-----\n' +
        'MIIFDDCCAvQCCQCWGBewlWbp0DANBgkqhkiG9w0BAQsFADBIMQswCQYDVQQGEwJE\n' +
        'RTEPMA0GA1UECAwGQmVybGluMQ8wDQYDVQQHDAZCZXJsaW4xFzAVBgNVBAMMDmxp\n' +
        'Y2Vuc2UubjhuLmlvMB4XDTIyMDYyNDA0MDkzM1oXDTQ5MTEwOTA0MDkzM1owSDEL\n' +
        'MAkGA1UEBhMCREUxDzANBgNVBAgMBkJlcmxpbjEPMA0GA1UEBwwGQmVybGluMRcw\n' +
        'FQYDVQQDDA5saWNlbnNlLm44bi5pbzCCAiIwDQYJKoZIhvcNAQEBBQADggIPADCC\n' +
        'AgoCggIBANK6Uj5CSyPaa2rf/bwH/AqzzINeWg4n94pECv4p8tsjy0+ZscodNn7Q\n' +
        'IvYW/6IUpax8fZvHCSqu0L7fsgLkw0+HDLlsln+ryI/3h1ElVZm67BfqOvhSfPj+\n' +
        'btxcP8EtS+6hbf74sqPEBFD0wikKhIPGdtBolp7rLXfTGs+5eMqSA5q4W1V+HUUU\n' +
        'zgaCdchzlHGTdIICNpbRozGeFTIIx1MPzMie+4zIs6i680efx2KJVZWsa2WuCSED\n' +
        '+b0gSRTgoKQ3B7JjxJKfzHdOdM6JxhkCjrJIkJCrZL7wFypn+wMHsVFrsw/80WkS\n' +
        'dwBj/RqCPzKcRwnxs0WNLhkyT/XCMS/1pNzPymdFADu2tEZjhFsa5ziIhdoQz8po\n' +
        'Efheyg3mOZDWyu6oyHCLH9lUlsNjV9zZ6RICgvHZvHPmPqOLPomy4Qymq2osuH+S\n' +
        '28rwEy0W3DCEfuS3dZHrSQfohGC9EAyyFAIPSGhTJSYJl0oNBZjpRM+jAm9ER7PM\n' +
        'K70/7g6z8uIgKa8/SD/Pezd/aWzxH/tOokAF5X5o5TzSOwVzqOJzY06EZC7CJnhs\n' +
        'sigAJTy1trZJBcZuWnKY5FqrK3QzjsQ9sWM0tRocfNOYmD21mubNo+RmMMo3kvYL\n' +
        'JoBfap/cuP6kx3OiRzBJ4luTEa6nRIYy+EjcPaN3+9L5iEZ9/NABAgMBAAEwDQYJ\n' +
        'KoZIhvcNAQELBQADggIBAIUpJ0Rh6C92cJqy6iehS8c77r1prj62gr6Acdy5QGh8\n' +
        'ax30ANDbPtkezVIIBk+0JjLkMxr3mwqCRsPgMshPcxhZwEafmZrWPPGRVsOfukB3\n' +
        'Gp10cIv1YmZ7AFY4i/izj5184+A+GjxrJBULQG1JfX4c7KW06Kaps85AaX/Tp0fl\n' +
        '/hZ6oUe48aSSTuAW38hWZ5jCpBqfFyQ15ablJZt0fB8NkXEdFUcUEt/OHJfqF5fP\n' +
        'Nt5PWC1AuBxzSbzjwabOy/M4r8m5CzCeXSIjqbcsAq52myMoxvNrjKRtucQ7CjqK\n' +
        'LR6WdhMf+NLvyTI+evXMoXpDzlevkCpz17uDiDt9XrmvLSf3Ff8mdTRwzVmOmHfc\n' +
        'Ruz5v7WBR4cpBR/sC+WxOw9heLMHgYhPyRUM4voo55EcJGusXr/iavblBAnI0GUw\n' +
        'uMNs3MjEmfbaBV1K0GKG5UToF9UmUxxVmTcRUZrAQX+yBkRezDTpOQuRnB58BDQP\n' +
        'A62lcgZlikzJw3lHue33qAudyUrUmjfHGll7GFQNeLO42jPMeM4NJiNIrBZ7j0uD\n' +
        '4slHdDi/44PHcKchvde/5k7ZJMi6OC1Vu+bOnKvzhnWQIOkHFQ55m13oslV2ozZT\n' +
        '/oqhn/8/LeV0ooZuYnBeRkRTveniUeho2OFe5xZvWSSfuBrbHt9+zBg+5MWPUTIJ\n' +
        '-----END CERTIFICATE-----'
    )

    //set defaults for not set config properties:
    this.config.autoRenewEnabled = this.config.autoRenewEnabled ?? true
    this.config.renewOnInit = this.config.renewOnInit ?? false
    this.config.offlineMode = this.config.offlineMode ?? false
    this.config.autoRenewOffset = this.config.autoRenewOffset ?? 60 * 60 * 72 // default: 72 hours
    this.config.server = this.config.server ?? 'https://license.n8n.io/v1'
  }

  log(msg: string, logLevel: LogLevel) {
    this.logger[logLevel](`[license SDK] ${msg}`)
  }

  async initialize() {
    if (this.initializationPromise) {
      return this.initializationPromise
    }

    this.initializationPromise = this._doInitialization()

    return await this.initializationPromise
  }

  /** Initializes the license manager */
  private async _doInitialization() {
    ok(!this.initializationPromise, 'Initialization already in progress')

    this.deviceFingerprint = await this.computeDeviceFingerprint()
    this.log(
      `initializing for deviceFingerprint ${this.deviceFingerprint}`,
      LogLevel.DEBUG
    )

    await this.initCert() // doesn't throw!

    // Clear and set up periodic timer for entitlement changes
    this.clearRepeatingTimer(this.upcomingEntitlementChangesCheckTimer)
    this.upcomingEntitlementChangesCheckTimer = this.setupRepeatingTimer(
      () => this.setTimerForNextEntitlementChange(),
      15 * 60 * 1000 // 15 minutes
    )

    // Perform initial renewal if conditions are met
    // If local cert has detachedEntitlementsCount marker, a force renewal makes sense as floating entitlements might get attached
    // Otherwise do the renewal only if the cert is older than 24 hours
    if (
      this.config.renewOnInit &&
      this.config.autoRenewEnabled &&
      this.hasCert() &&
      (this.licenseCert.detachedEntitlementsCount > 0 ||
        this.isOlderThan(this.licenseCert.issuedAt, 24 * 60 * 60 * 1000)) // 24 hours
    ) {
      await this.renewalCron({ force: true })
    } else {
      this.log(
        'Skipping renewal on init because cert was issued less than 24 hours ago or not initialized',
        LogLevel.DEBUG
      )
    }

    // Set up periodic renewal if enabled
    if (this.config.autoRenewEnabled) {
      this.clearRepeatingTimer(this.renewalTimer)
      this.renewalTimer = this.setupRepeatingTimer(
        () => this.renewalCron({ force: false }),
        15 * 60 * 1000 // 15 minutes
      )
    }
  }

  private setupSingleTimer(
    callback: () => void,
    interval: number
  ): SingleTimer {
    return setTimeout(callback, interval)
  }

  clearSingleTimer(timer: SingleTimer | undefined) {
    if (timer) {
      clearTimeout(timer)
    }
  }

  private setupRepeatingTimer(
    callback: () => void,
    interval: number
  ): RepeatingTimer {
    return setInterval(callback, interval)
  }

  clearRepeatingTimer(timer: RepeatingTimer | undefined) {
    if (timer) {
      clearInterval(timer)
    }
  }

  private isOlderThan(date: Date, durationInMs: number) {
    return Date.now() - date.getTime() > durationInMs
  }

  async reload() {
    await this.reset()
    await this.initialize()
  }

  async reset() {
    // Make sure initialization is not ongoing before resetting
    if (this.initializationPromise) {
      await this.initializationPromise
    }
    this.clearRepeatingTimer(this.renewalTimer)
    this.renewalTimer = undefined
    this.clearRepeatingTimer(this.upcomingEntitlementChangesCheckTimer)
    this.upcomingEntitlementChangesCheckTimer = undefined
    this.clearSingleTimer(this.entitlementChangeTimer)
    this.entitlementChangeTimer = undefined

    this.licenseCert = undefined
    this.deviceFingerprint = undefined
    this.initializationPromise = undefined
  }

  async computeDeviceFingerprint() {
    if (
      this.config.deviceFingerprint &&
      typeof this.config.deviceFingerprint === 'function'
    ) {
      return await this.config.deviceFingerprint()
    }
    return await machineId()
  }

  async activate(reservationId: string) {
    if (!this.isInitialized) {
      throw new Error('activation failed because SDK was not yet initialized')
    }

    if (this.isShuttingDown) {
      throw new Error('activation failed because SDK is shutting down')
    }

    if (this.config.offlineMode) {
      throw new Error('activation failed because SDK is in offline mode')
    }

    const postData: {
      reservationId: string
      tenantId: number
      productIdentifier: string
      deviceFingerprint: string
      consumerId?: string
      renewalToken?: string
    } = {
      reservationId,
      tenantId: this.config.tenantId,
      productIdentifier: this.config.productIdentifier,
      deviceFingerprint: this.deviceFingerprint!,
    }

    if (this.hasCert()) {
      postData.consumerId = this.licenseCert.consumerId
      postData.renewalToken = this.licenseCert.renewalToken
    }

    type ResponseDataFormat = {
      licenseKey?: string
      x509?: string
      detachedEntitlementsCount?: number
      message?: string
      errorId?: string
    }

    type PostRequestResponse = Awaited<
      ReturnType<typeof postRequest<ResponseDataFormat>>
    >

    let response: PostRequestResponse

    try {
      response = await postRequest<{
        licenseKey?: string
        x509?: string
        message?: string
        errorId?: string
      }>(`${this.config.server!}/activate`, postData, { timeoutInMs: 10000 })
    } catch (error) {
      this.log(
        'license activation failed: ' + (error as Error).message,
        LogLevel.WARN
      )
      throw new Error((error as Error).message)
    }

    if (response.status == 200) {
      if (
        !response.data.hasOwnProperty('licenseKey') ||
        !response.data.hasOwnProperty('x509')
      ) {
        this.log('unexpected server response', LogLevel.WARN)
        throw new Error('unexpected server response')
      }
      const containerStr = this.stringifyCertContainer({
        licenseKey: response.data.licenseKey,
        x509: response.data.x509,
      } as TLicenseContainer)
      await this.config.saveCertStr(containerStr)
      await this.initCert()
      this.log('license successfully activated', LogLevel.INFO)
      if (response.data.detachedEntitlementsCount ?? 0 > 0) {
        this.log(
          `skipped importing ${response.data.detachedEntitlementsCount} floating entitlements which are currently in use elsewhere`,
          LogLevel.INFO
        )
      }
    } else {
      const errorMsg = `license activation failed: ${
        response.data.message ?? 'unknown reason'
      }`
      const e = new Error(errorMsg) as Error & { errorId?: string }

      if (response.data.errorId) {
        e.errorId = response.data.errorId
      }

      this.log(errorMsg, LogLevel.WARN)
      throw e
    }
  }

  async renew() {
    return this._renew({ cause: 'request' })
  }

  async _renew({
    detachFloatingEntitlements = false,
    cause = 'unknown',
  }: {
    detachFloatingEntitlements?: Boolean
    cause?: 'startup' | 'shutdown' | 'request' | 'auto' | 'unknown'
  } = {}) {
    if (!this.hasCert()) {
      throw new Error('renewal failed because current cert is not initialized')
    }

    if (this.licenseCert.isEphemeral) {
      return
    }

    if (this.isTerminated()) {
      throw new Error('renewal failed because current cert was terminated')
    }

    if (this.config.offlineMode) {
      throw new Error('renewal failed because SDK is in offline mode')
    }

    if (this.config.tenantId !== this.licenseCert.tenantId) {
      throw new Error('renewal failed because tenant ID does not match')
    }

    // error out if device fingerprint does not match but allow renewal on explicit request
    // as the consumer might have been changed to more permissive settings on the server
    if (
      cause !== 'request' &&
      this.licenseCert.deviceLock &&
      this.deviceFingerprint !== this.licenseCert.deviceFingerprint
    ) {
      throw new Error(
        'renewal failed because device fingerprint does not match'
      )
    }

    const [usageMetrics, passthroughData] = await Promise.all([
      this.config.collectUsageMetrics ? this.config.collectUsageMetrics() : [],
      this.config.collectPassthroughData
        ? this.config.collectPassthroughData()
        : {},
    ])

    type ResponseDataFormat = {
      licenseKey?: string
      x509?: string
      detachedEntitlementsCount?: number
      message?: string
      errorId?: string
    }

    type PostRequestResponse = Awaited<
      ReturnType<typeof postRequest<ResponseDataFormat>>
    >

    let response: PostRequestResponse

    try {
      response = await postRequest<ResponseDataFormat>(
        `${this.config.server!}/renew`,
        {
          consumerId: this.licenseCert.consumerId,
          tenantId: this.config.tenantId,
          deviceFingerprint: this.deviceFingerprint,
          productIdentifier: this.config.productIdentifier,
          renewalToken: this.licenseCert.renewalToken,
          detachFloatingEntitlements,
          cause,
          usageMetrics,
          passthroughData,
        },
        { timeoutInMs: 10000 }
      )
    } catch (error) {
      this.log(
        'license renewal failed: ' + (error as Error).message,
        LogLevel.WARN
      )
      throw new Error((error as Error).message)
    }

    const responseData = response.data

    if (response.status == 200) {
      if (
        !responseData.hasOwnProperty('licenseKey') ||
        !responseData.hasOwnProperty('x509')
      ) {
        this.log(
          'license renewal failed: unexpected server response',
          LogLevel.WARN
        )
        throw new Error('unexpected server response')
      }
      const containerStr = this.stringifyCertContainer({
        licenseKey: responseData.licenseKey,
        x509: responseData.x509,
      } as TLicenseContainer)
      await this.config.saveCertStr(containerStr)
      this.log('license successfully renewed', LogLevel.DEBUG)
      if (responseData.detachedEntitlementsCount ?? 0 > 0) {
        this.log(
          `skipped importing ${responseData.detachedEntitlementsCount} floating entitlements which are currently in use elsewhere`,
          LogLevel.INFO
        )
      }
      await this.initCert()
    } else {
      const errorMsg = `license renewal failed: ${
        responseData.message ?? 'unknown reason'
      }`

      this.log(errorMsg, LogLevel.WARN)

      const e = new Error(errorMsg) as Error & { errorId?: string }

      if (responseData.errorId) {
        e.errorId = responseData.errorId

        if (
          ['NOT_FOUND', 'CONSUMER_TERMINATED'].includes(responseData.errorId)
        ) {
          this.log(
            'license was terminated by the server. Deleting local cert.',
            LogLevel.WARN
          )
          await this.config.saveCertStr('')
          await this.initCert()
        }
      }

      throw e
    }
  }

  private hasCert(): this is { licenseCert: TLicenseCertObj } {
    return !!this.licenseCert
  }

  isTerminated(): boolean {
    if (!this.hasCert()) {
      throw new Error('Cert is not initialized')
    }

    const now = new Date()

    return this.licenseCert.terminatesAt < now
  }

  getExpiryDate(): Date {
    if (!this.hasCert()) {
      throw new Error('Cert is not initialized')
    }

    return this.licenseCert.expiresAt
  }

  getTerminationDate(): Date {
    if (!this.hasCert()) {
      throw new Error('Cert is not initialized')
    }

    return this.licenseCert.terminatesAt
  }

  isValid(useLogger = true): boolean {
    const error = (msg: string) => {
      if (useLogger) {
        this.log(msg, LogLevel.ERROR)
      }
    }

    if (!this.hasCert()) {
      error('cert is invalid because it is undefined')
      return false
    }

    const cert = this.licenseCert as TLicenseCertObj
    const now = new Date()

    if (cert.expiresAt < now) {
      error('cert is invalid because it has expired')
      return false
    }

    if (cert.terminatesAt < now) {
      error('cert is invalid because it was terminated')
      return false
    }

    //allow clocks to be out of sync by no more than 300,000 ms / = 5 minutes
    if (cert.createdAt.getTime() - 300000 > now.getTime()) {
      error('cert is invalid because system clock is out of sync')
      return false
    }

    if (cert.deviceLock && this.deviceFingerprint !== cert.deviceFingerprint) {
      error('cert is invalid because device fingerprint does not match')
      return false
    }

    if (this.config.tenantId !== cert.tenantId) {
      error('cert is invalid because tenant ID does not match')
      return false
    }

    return true
  }

  hasFeatureEnabled(
    feature: string,
    requireValidCert: boolean = true
  ): boolean {
    return this.getFeatureValue(feature, requireValidCert) ? true : false
  }

  hasFeatureDefined(
    feature: string,
    requireValidCert: boolean = true
  ): boolean {
    return this.getFeatureValue(feature, requireValidCert) !== undefined
      ? true
      : false
  }

  hasQuotaLeft(quotaFeatureName: string, currentConsumption: number): boolean {
    const quota = this.getFeatureValue(quotaFeatureName)

    if (quota === undefined) {
      return false
    }

    if (typeof quota !== 'number') {
      throw new Error(
        `${quotaFeatureName} cannot be used as quota as it is not numeric`
      )
    }

    // -1 means unlimited quota!
    if (quota === -1) {
      return true
    }

    return Math.ceil(currentConsumption) < Math.ceil(quota)
  }

  getFeatureValue(
    feature: string,
    requireValidCert: boolean = true
  ): undefined | boolean | number | string {
    if (!this.hasCert()) {
      return undefined
    }

    if (requireValidCert && !this.isValid()) {
      return undefined
    }

    const currentFeatures = this.getFeatures()

    if (!currentFeatures.hasOwnProperty(feature)) {
      return undefined
    }

    return currentFeatures[feature]
  }

  private updateCurrentFeatures() {
    if (!this.hasCert()) {
      return
    }

    const now = new Date()

    if (
      this.licenseCert.expiresAt < now ||
      this.licenseCert.terminatesAt < now
    ) {
      this.currentFeatures = {} as TFeatures
    }

    this.currentFeatures = this.licenseCert.entitlements
      .filter(
        (entitlement) =>
          entitlement.validFrom <= now && entitlement.validTo > now
      )
      .sort(
        (entitlement1, entitlement2) =>
          entitlement1.validFrom.getTime() - entitlement2.validFrom.getTime()
      )
      .reduce((currentFeatures, entitlement) => {
        return {
          ...currentFeatures,
          ...entitlement.features,
          ...entitlement.featureOverrides,
        }
      }, {} as TFeatures)
  }

  getFeatures() {
    if (!this.hasCert()) {
      return {}
    }

    return this.currentFeatures ?? {}
  }

  getCurrentEntitlements() {
    if (!this.hasCert()) {
      return []
    }

    const now = new Date()

    return this.licenseCert.entitlements.filter(
      (entitlement) => entitlement.validFrom <= now && entitlement.validTo > now
    )
  }

  getManagementJwt() {
    if (!this.licenseCert) {
      return ''
    }

    return this.licenseCert.managementJwt
  }

  async getCertStr(): Promise<TLicenseBlock> {
    return this.config.loadCertStr()
  }

  getConsumerId() {
    return this.licenseCert?.consumerId
  }

  isRenewalDue(): boolean {
    if (!this.licenseCert || this.licenseCert.isEphemeral) {
      return false
    }

    const now = Date.now()
    const mins15inMs = 1000 * 60 * 15
    const mins20inMs = 1000 * 60 * 20
    const expiry = this.licenseCert.expiresAt.getTime()
    const issuedAt = this.licenseCert.issuedAt.getTime()
    const termination = this.licenseCert.terminatesAt.getTime()

    //consumerRenewalDue is TRUE when current time is close enough to consumer expiry and not after consumer termination
    const consumerRenewalDue =
      now > expiry - this.config.autoRenewOffset! * 1000 && now < termination

    //entitlementRenewalDue is TRUE when one currently valid entitlement will expire in the next 15 minutes
    //and the Cert was issued more than 20 minutes ago
    const entitlementRenewalDue =
      this.getCurrentEntitlements().find(
        (entitlement) =>
          now >= entitlement.validTo.getTime() - mins15inMs &&
          now <= entitlement.validTo.getTime()
      ) !== undefined && issuedAt < now - mins20inMs

    return consumerRenewalDue || entitlementRenewalDue
  }

  private formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60

    return `${hours}h ${minutes}m ${remainingSeconds}s`
  }

  toString() {
    return (
      `## CONSUMER CONFIG ##\n` +
      `tenantId: ${this.config.tenantId ?? '<n/a>'}\n` +
      `productIdentifier: ${this.config.productIdentifier ?? '<n/a>'}\n` +
      `deviceFingerprint: ${this.deviceFingerprint ?? '<n/a>'}\n` +
      `autoRenewalEnabled: ${
        this.config.autoRenewEnabled ? 'true' : 'false (!)'
      }\n` +
      `autoRenewalOffset: ${
        this.config.autoRenewOffset
      } (= ${this.formatDuration(this.config.autoRenewOffset ?? 0)})\n` +
      '--\n' +
      `## LICENSE CERT ##\n` +
      `version: ${this.licenseCert?.version ?? '<n/a>'}\n` +
      `tenantId: ${this.licenseCert?.tenantId ?? '<n/a>'} ${
        this.licenseCert?.tenantId !== this.config.tenantId ? '(!)' : ''
      }\n` +
      `consumerId: ${this.licenseCert?.consumerId ?? '<n/a>'}\n` +
      `deviceFingerprint: ${this.licenseCert?.deviceFingerprint ?? '<n/a>'} ${
        this.licenseCert?.deviceFingerprint !== this.deviceFingerprint
          ? '(!)'
          : ''
      }\n` +
      `createdAt: ${this.licenseCert?.createdAt ?? '<n/a>'}\n` +
      `issuedAt: ${this.licenseCert?.issuedAt ?? '<n/a>'}\n` +
      `expiresAt: ${this.licenseCert?.expiresAt ?? '<n/a>'}\n` +
      `terminatesAt: ${this.licenseCert?.terminatesAt ?? '<n/a>'}\n` +
      `isEphemeral: ${this.licenseCert?.isEphemeral ?? '<n/a>'}\n` +
      `isValid: ${this.isValid(false)}\n` +
      `isRenewalDue: ${this.isRenewalDue()}\n` +
      `detachedEntitlementsCount: ${
        this.licenseCert?.detachedEntitlementsCount ?? '<n/a>'
      }\n` +
      `entitlements: ${this.licenseCert?.entitlements.length ?? 0}\n` +
      this.licenseCert?.entitlements
        .map((entitlement, index) => {
          return (
            '--\n' +
            `## ENTITLEMENT ${index + 1} ##\n` +
            `id: ${entitlement.id}\n` +
            `productId: ${entitlement.productId}\n` +
            `validFrom: ${entitlement.validFrom}\n` +
            `validTo: ${entitlement.validTo}\n` +
            `features: ${JSON.stringify(entitlement.features)}\n` +
            `featureOverrides: ${JSON.stringify(
              entitlement.featureOverrides
            )}\n`
          )
        })
        .join('')
    )
  }

  private triggerOnFeatureChangeCallback() {
    if (this.config.onFeatureChange) {
      this.config.onFeatureChange(this.currentFeatures ?? {})
    }
  }

  private setTimerForNextEntitlementChange() {
    if (!this.hasCert()) {
      return
    }

    const now = Date.now()
    const offsets = new Set<number>()

    offsets.add(this.licenseCert.expiresAt.getTime() - now)
    offsets.add(this.licenseCert.terminatesAt.getTime() - now)

    const upcomingEventOffsets = [
      ...this.licenseCert.entitlements.reduce((offsets, entitlement) => {
        offsets.add(entitlement.validFrom.getTime() - now)
        offsets.add(entitlement.validTo.getTime() - now)
        return offsets
      }, offsets),
    ]
      .filter((offset) => offset >= 0 && offset <= 15 * 60 * 1000) // keep if offset is within the next 15 minutes
      .sort((a, b) => a - b)

    //console.dir(upcomingEventOffsets.map((offset) => offset / 1000 / 60))

    if (upcomingEventOffsets.length === 0) {
      return
    }

    const timeTillNextEventInMs = upcomingEventOffsets[0]

    this.clearSingleTimer(this.entitlementChangeTimer)
    this.entitlementChangeTimer = this.setupSingleTimer(() => {
      this.updateCurrentFeatures()
      this.triggerOnFeatureChangeCallback()
      this.setTimerForNextEntitlementChange()
    }, timeTillNextEventInMs + 1000)
  }

  private async renewalCron({ force }: { force: boolean }) {
    if (force || this.isRenewalDue()) {
      this.log('attempting license renewal', LogLevel.DEBUG)
      try {
        await this._renew({ cause: force ? 'startup' : 'auto' })
      } catch (e) {}
    }
  }

  private async initCert() {
    if (this.isShuttingDown) {
      return
    }

    try {
      const containerStr = await this.getCertStr()
      if (!containerStr) {
        this.licenseCert = undefined
        return
      }
      if (containerStr.length < 50) {
        throw new Error('cert string is undefined or too short')
      }
      const { x509, licenseKey } =
        this.parseLicenseCertContainerStr(containerStr)
      this.x509Cert = new X509Certificate(x509)
      const hasApprovedIssuer = this.x509Cert.checkIssued(this.getIssuerCert())
      if (!hasApprovedIssuer) {
        throw new Error('cert was not issued by an approved issuer')
      }
      const publicKey = this.x509Cert.publicKey.export({
        format: 'pem',
        type: 'pkcs1',
      })
      this.key = new NodeRSA(publicKey)
      this.licenseCert = this.parseLicenseKeyStr(licenseKey)
      this.updateCurrentFeatures()
      this.setTimerForNextEntitlementChange()
    } catch (e) {
      this.log(
        `cert could not be initialized. ${e instanceof Error ? e.message : ''}`,
        LogLevel.ERROR
      )
    }
  }

  private stringifyCertContainer(container: TLicenseContainer): string {
    const containerStr = JSON.stringify(container)
    return Buffer.from(containerStr).toString('base64')
  }

  private parseLicenseCertContainerStr(
    stringifiedContainer: string
  ): TLicenseContainer {
    const containerStr = Buffer.from(stringifiedContainer, 'base64').toString(
      'ascii'
    )
    const container = JSON.parse(containerStr)
    if (
      !container.hasOwnProperty('licenseKey') ||
      !container.hasOwnProperty('x509')
    ) {
      throw new Error('license cert container could not be parsed')
    }

    return container as TLicenseContainer
  }

  private parseLicenseKeyStr(certStr: string): TLicenseCertObj {
    const certPayload = this.validateLicenseKey(certStr)

    const cert = { ...certPayload } as TLicenseCertObj

    // turn timestamp strings into Date objects
    cert.createdAt = new Date(certPayload.createdAt)
    cert.issuedAt = new Date(certPayload.issuedAt)
    cert.expiresAt = new Date(certPayload.expiresAt)
    cert.terminatesAt = new Date(certPayload.terminatesAt)

    // make sure detachedEntitlementsCount is a number (certs issues before this field was introduced will have it missing)
    cert.detachedEntitlementsCount = certPayload.detachedEntitlementsCount ?? 0

    cert.entitlements = cert.entitlements.map((entitlement) => {
      entitlement.validFrom = new Date(entitlement.validFrom)
      entitlement.validTo = new Date(entitlement.validTo)
      return entitlement
    })

    return cert
  }

  private validateLicenseKey(licenseKey: string) {
    //remove all line breaks
    licenseKey = licenseKey.replace(/(\r\n|\n|\r)/gm, '')

    //extract parts
    const match = licenseKey.match(
      /^-----BEGIN LICENSE KEY-----(?<encryptedSymmetricKey>.+\|\|)(?<encryptedData>.+)\|\|(?<signature>.+)-----END LICENSE KEY-----$/
    )

    if (!match) {
      throw new Error('license key could not be parsed')
    }

    const encryptedSymmetricKey = match.groups!.encryptedSymmetricKey
    const payload = match.groups!.encryptedData
    const signature = match.groups!.signature

    let symmetricKey, decryptedData

    //decrypt the random symmetric key
    try {
      symmetricKey = this.key!.decryptPublic(encryptedSymmetricKey, 'utf8')
    } catch (e) {
      throw new Error('Invalid data: Could not extract symmetric key')
    }

    //decrypt the payload
    try {
      decryptedData = CryptoJS.AES.decrypt(payload, symmetricKey).toString(
        CryptoJS.enc.Utf8
      )
    } catch (e) {
      throw new Error('Invalid Data: Could not decrypt data with key found')
    }

    //verify the signature.
    if (
      this.key!.verify(Buffer.from(decryptedData), signature, 'utf8', 'base64')
    ) {
      //return the decrypted data.
      return JSON.parse(decryptedData)
    } else {
      throw new Error('License Key signature invalid')
    }
  }

  public getIssuerCert() {
    return this.x509IssuerCert
  }

  public async shutdown() {
    this.isShuttingDown = true

    this.clearRepeatingTimer(this.renewalTimer)
    this.clearRepeatingTimer(this.upcomingEntitlementChangesCheckTimer)
    this.clearSingleTimer(this.entitlementChangeTimer)

    if (!this.hasCert()) {
      return
    }

    const hasFloatableEntitlements = this.licenseCert.entitlements.some(
      (entitlement) => entitlement.isFloatable ?? false
    )

    if (hasFloatableEntitlements) {
      await this._renew({ detachFloatingEntitlements: true, cause: 'shutdown' })
    }
  }
}
